import {
  usePrivateLessonBookingAttendanceQuery,
  usePrivateLessonBookingQuery,
} from "@/modules/private-lessons/presentation/api/queries";
import { isMultiMemberPackageKind } from "@/modules/private-lessons/domain/lib/package-kind";
import { formatPrivateLessonBookingPlace } from "@/modules/private-lessons/domain/lib/booking-display";
import { Button, Checkbox, ScrollArea, toast } from "@devhop/ui";
import { CheckIcon, Loader2, Undo2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  useBookingInstructorAttendeesQuery,
  useBulkUndoCheckInPrivateBookingMutation,
  useCheckInPrivateBookingMutation,
} from "@/modules/attendance/presentation/api/queries";
import { AddGuestInstructorDialog } from "@/modules/attendance/presentation/ui/AddGuestInstructorDialog";
import { AddSubstituteInstructorDialog } from "@/modules/attendance/presentation/ui/AddSubstituteInstructorDialog";

function formatCheckedInAt(checkedInAt: string): string {
  try {
    const d = new Date(checkedInAt);
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h < 12 ? "AM" : "PM";
    const hour = h % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ampm}`;
  } catch {
    return checkedInAt;
  }
}

type CheckInPrivateFormProps = {
  bookingId: string;
  studentName: string;
  studentMemberId?: string;
  instructorIds: string[];
  instructorNames: string[];
  packageKind?: "individual" | "group";
  onSuccess: () => void;
  onCheckInComplete?: () => void;
  onCancel: () => void;
};

export function CheckInPrivateForm({
  bookingId,
  studentName,
  studentMemberId,
  instructorIds,
  instructorNames,
  packageKind: packageKindProp,
  onSuccess,
  onCheckInComplete,
  onCancel,
}: CheckInPrivateFormProps) {
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<
    Set<string>
  >(new Set());
  const [selectedUndoInstructorIds, setSelectedUndoInstructorIds] = useState<
    Set<string>
  >(new Set());
  const [selectedUndoAttendanceIds, setSelectedUndoAttendanceIds] = useState<
    Set<string>
  >(new Set());
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [substituteDialogOpen, setSubstituteDialogOpen] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);

  const { data: booking } = usePrivateLessonBookingQuery(bookingId);
  const packageKind = packageKindProp ?? booking?.packageKind ?? "individual";
  const isGroup = isMultiMemberPackageKind(packageKind);
  const isCompletedBooking = booking?.status === "completed";
  const members = booking?.members ?? [];

  const checkInMutation = useCheckInPrivateBookingMutation();
  const bulkUndoCheckInMutation = useBulkUndoCheckInPrivateBookingMutation();
  const instructorAttendeesQuery =
    useBookingInstructorAttendeesQuery(bookingId);
  const attendanceQuery = usePrivateLessonBookingAttendanceQuery(bookingId);

  const attendances = attendanceQuery.data?.records ?? [];
  const checkedInStudentIds = useMemo(
    () => new Set(attendances.map((a) => a.studentId)),
    [attendances],
  );

  const instructorAttendees = instructorAttendeesQuery.data?.attendees ?? [];
  const instructors = instructorIds.map((id, i) => ({
    id,
    name: instructorNames[i] ?? "",
  }));
  const instructorsNotCheckedIn = instructors.filter(
    (inst) => !instructorAttendees.some((a) => a.instructorId === inst.id),
  );

  const toggleInstructor = useCallback((id: string) => {
    setSelectedInstructorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleUndoInstructor = useCallback((id: string) => {
    setSelectedUndoInstructorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleMember = useCallback((id: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleUndoAttendance = useCallback((id: string) => {
    setSelectedUndoAttendanceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkUndo = useCallback(async () => {
    const instructorCheckInIds = Array.from(selectedUndoInstructorIds);
    const attendanceIds = Array.from(selectedUndoAttendanceIds);

    if (attendanceIds.length === 0 && instructorCheckInIds.length === 0) {
      toast.error("ກະລຸນາເລືອກນັກຮຽນ ຫຼື ຄູສອນຢ່າງໜ້ອຍ 1 ຄົນ");
      return;
    }

    try {
      let studentSuccess = 0;
      let studentFail = 0;

      for (const attendanceId of attendanceIds) {
        const result = await bulkUndoCheckInMutation.mutateAsync({
          bookingId,
          attendanceId,
          instructorCheckInIds:
            attendanceIds.indexOf(attendanceId) === 0 &&
            instructorCheckInIds.length > 0
              ? instructorCheckInIds
              : undefined,
        });
        if (result.studentSucceeded) studentSuccess += 1;
        if (result.studentFailed) studentFail += 1;
      }

      if (
        attendanceIds.length === 0 &&
        instructorCheckInIds.length > 0
      ) {
        const result = await bulkUndoCheckInMutation.mutateAsync({
          bookingId,
          instructorCheckInIds,
        });
        const instructorSuccessCount = result.instructorSucceeded?.length ?? 0;
        if (instructorSuccessCount > 0) {
          toast.success(`ຍົກເລີກການກວດຊື່ຄູ ${instructorSuccessCount} ຄົນສຳເລັດ`);
          onSuccess();
        }
        return;
      }

      if (studentSuccess > 0) {
        setSelectedUndoAttendanceIds(new Set());
        setSelectedUndoInstructorIds(new Set());
        toast.success(`ຍົກເລີກການກວດຊື່ນັກຮຽນ ${studentSuccess} ຄົນສຳເລັດ`);
        onSuccess();
      }
      if (studentFail > 0) {
        toast.error(`ຍົກເລີກການກວດຊື່ບໍ່ສຳເລັດ ${studentFail} ລາຍການ`);
      }
    } catch {
      //
    }
  }, [
    bookingId,
    selectedUndoAttendanceIds,
    selectedUndoInstructorIds,
    bulkUndoCheckInMutation,
    onSuccess,
  ]);

  const handleConfirm = async () => {
    try {
      const instructorIdsArray = Array.from(selectedInstructorIds);
      const memberStudentIds = isGroup
        ? Array.from(selectedMemberIds)
        : undefined;

      if (isCompletedBooking) {
        if (instructorIdsArray.length === 0) {
          toast.error("ກະລຸນາເລືອກຄູສອນ");
          return;
        }
        await checkInMutation.mutateAsync({
          bookingId,
          instructorIds: instructorIdsArray,
        });
        setSelectedInstructorIds(new Set());
        toast.success("ກວດຊື່ຄູສອນສຳເລັດ");
        if (onCheckInComplete) onCheckInComplete();
        else onSuccess();
        return;
      }

      if (isGroup && (!memberStudentIds || memberStudentIds.length === 0)) {
        if (attendances.length === 0) {
          toast.error("ກະລຸນາເລືອກສະມາຊິກທີ່ມາຮຽນ");
          return;
        }
        if (instructorIdsArray.length === 0) {
          toast.error("ກະລຸນາເລືອກສະມາຊິກ ຫຼື ຄູສອນ");
          return;
        }
      }

      await checkInMutation.mutateAsync({
        bookingId,
        instructorIds:
          instructorIdsArray.length > 0 ? instructorIdsArray : undefined,
        memberStudentIds: isGroup
          ? memberStudentIds && memberStudentIds.length > 0
            ? memberStudentIds
            : undefined
          : undefined,
      });

      setSelectedInstructorIds(new Set());
      setSelectedMemberIds(new Set());

      if (onCheckInComplete) onCheckInComplete();
      else onSuccess();
    } catch {
      //
    }
  };

  const isSubmitting =
    checkInMutation.isPending || bulkUndoCheckInMutation.isPending;

  const canConfirmCheckIn = (() => {
    if (isSubmitting) return false;
    if (isCompletedBooking) {
      return selectedInstructorIds.size > 0;
    }
    if (isGroup) {
      if (selectedMemberIds.size > 0) return true;
      if (attendances.length > 0 && instructorsNotCheckedIn.length > 0) {
        return selectedInstructorIds.size > 0;
      }
      return false;
    }
    if (attendances.length === 0) return true;
    if (instructorsNotCheckedIn.length > 0) {
      return selectedInstructorIds.size > 0;
    }
    return false;
  })();

  const membersNotCheckedIn = members.filter(
    (m) => !checkedInStudentIds.has(m.studentId),
  );

  return (
    <div className="flex flex-col gap-4">
      {booking && (
        <p className="rounded-md border bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
          ສະຖານທີ່:{" "}
          {formatPrivateLessonBookingPlace({
            locationKind: booking.locationKind,
            offSiteDistance: booking.offSiteDistance,
            offSiteLabel: booking.offSiteLabel,
            roomName: booking.roomName,
          })}
        </p>
      )}
      {!isCompletedBooking && isGroup ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-medium text-sm">ສະມາຊິກໃນກຸ່ມ</h3>
            <span className="text-muted-foreground text-xs">
              {attendances.length}/{members.length} ກວດຊື່ແລ້ວ
            </span>
          </div>
          {attendances.map((a) => (
            <label
              key={a.id}
              htmlFor={`undo-member-${a.id}`}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-green-500/30 bg-green-500/5 p-3"
            >
              <div className="flex-1">
                <div className="font-medium">{a.studentName}</div>
                <div className="text-muted-foreground text-xs">
                  #{a.studentMemberId} · {formatCheckedInAt(a.checkedInAt)}
                </div>
              </div>
              <Checkbox
                id={`undo-member-${a.id}`}
                checked={selectedUndoAttendanceIds.has(a.id)}
                onCheckedChange={() => toggleUndoAttendance(a.id)}
                disabled={isSubmitting}
              />
            </label>
          ))}
          {membersNotCheckedIn.length > 0 && (
            <div className="rounded-md border">
              <ul className="space-y-0 p-2">
                {membersNotCheckedIn.map((m) => (
                  <li key={m.studentId}>
                    <label
                      htmlFor={`check-member-${m.studentId}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-muted"
                    >
                      <Checkbox
                        id={`check-member-${m.studentId}`}
                        checked={selectedMemberIds.has(m.studentId)}
                        onCheckedChange={() => toggleMember(m.studentId)}
                        disabled={isSubmitting}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{m.studentName}</div>
                        <div className="text-muted-foreground text-xs">
                          #{m.studentMemberId}
                        </div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : !isCompletedBooking ? (
        attendances[0] && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4">
            <label
              htmlFor="undo-student-attendance"
              className="flex cursor-pointer items-center justify-between gap-2"
            >
              <div className="flex-1">
                <div className="mb-2 inline-block rounded bg-green-600/20 px-2 py-0.5 font-medium text-green-700 text-xs dark:text-green-400">
                  ນັກຮຽນຈາກການຈອງ
                </div>
                <div className="font-medium">{studentName}</div>
                <div className="text-muted-foreground text-sm">
                  Member ID: #{studentMemberId ?? "-"}
                </div>
                <div className="mt-2 text-muted-foreground text-xs">
                  ກວດຊື່ແລ້ວ: {formatCheckedInAt(attendances[0].checkedInAt)}
                </div>
              </div>
              <Checkbox
                id="undo-student-attendance"
                checked={selectedUndoAttendanceIds.has(attendances[0].id)}
                onCheckedChange={() =>
                  toggleUndoAttendance(attendances[0].id)
                }
                disabled={isSubmitting}
              />
            </label>
          </div>
        )
      ) : isCompletedBooking && attendances.length > 0 ? (
        <div className="space-y-2">
          <h3 className="font-medium text-sm">ນັກຮຽນທີ່ກວດຊື່ແລ້ວ</h3>
          {attendances.map((a) => (
            <div
              key={a.id}
              className="rounded-lg border border-green-500/30 bg-green-500/5 p-3"
            >
              <div className="font-medium">{a.studentName}</div>
              <div className="text-muted-foreground text-xs">
                {formatCheckedInAt(a.checkedInAt)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {instructors.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-sm">ຄູສອນ</h3>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 font-medium text-xs">
                {instructorAttendees.length}/{instructors.length}
              </span>
            </div>
            {instructorsNotCheckedIn.length > 0 && (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setSelectedInstructorIds(
                      new Set(instructorsNotCheckedIn.map((i) => i.id)),
                    );
                  }}
                >
                  ເລືອກທັງໝົດ
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setSelectedInstructorIds(new Set())}
                >
                  ລ້າງ
                </Button>
              </div>
            )}
          </div>
          <div className="mb-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 flex-1 text-xs"
                onClick={() => setSubstituteDialogOpen(true)}
              >
                + ເລືອກຄູສອນແທນ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 flex-1 text-xs"
                onClick={() => setGuestDialogOpen(true)}
              >
                + ເພີ່ມຄູສອນຊົ່ວຄາວ
              </Button>
            </div>
          </div>
          <ScrollArea className="max-h-[min(280px,40vh)] rounded-md border border-border">
            <div className="space-y-2 p-2">
              {instructorAttendees.map((a) => (
                <label
                  key={a.id}
                  htmlFor={`undo-instructor-${a.id}`}
                  className="flex cursor-pointer gap-2 rounded-md border border-green-500/30 bg-green-500/5 px-2 py-2 text-sm hover:bg-green-500/10"
                >
                  <Checkbox
                    id={`undo-instructor-${a.id}`}
                    className="mt-0.5 shrink-0"
                    checked={selectedUndoInstructorIds.has(a.id)}
                    onCheckedChange={() => toggleUndoInstructor(a.id)}
                    disabled={isSubmitting}
                  />
                  <div className="min-w-0 flex-1">
                    <span className="block font-medium text-sm">
                      {a.instructorName}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {formatCheckedInAt(a.checkedInAt)}
                    </span>
                  </div>
                </label>
              ))}
              {instructorsNotCheckedIn.length > 0 && (
                <ul className="space-y-0">
                  {instructorAttendees.length > 0 && (
                    <li className="py-1">
                      <span className="text-muted-foreground text-xs">
                        ຍັງບໍ່ໄດ້ກວດຊື່
                      </span>
                    </li>
                  )}
                  {instructorsNotCheckedIn.map((inst) => (
                    <li key={inst.id}>
                      <label
                        htmlFor={`check-instructor-${inst.id}`}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-2 hover:bg-muted"
                      >
                        <Checkbox
                          id={`check-instructor-${inst.id}`}
                          className="shrink-0"
                          checked={selectedInstructorIds.has(inst.id)}
                          onCheckedChange={() => toggleInstructor(inst.id)}
                          disabled={isSubmitting}
                        />
                        <div className="min-w-0 flex-1 truncate font-medium text-sm">
                          {inst.name}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {(selectedUndoAttendanceIds.size > 0 ||
          selectedUndoInstructorIds.size > 0) && (
          <Button
            type="button"
            variant="destructive"
            className="gap-2"
            onClick={handleBulkUndo}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                ກຳລັງຍົກເລີກ...
              </>
            ) : (
              <>
                <Undo2Icon className="h-4 w-4" />
                ຍົກເລີກການກວດຊື່
              </>
            )}
          </Button>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
          <Button
            className="gap-2"
            onClick={handleConfirm}
            disabled={!canConfirmCheckIn}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                ກຳລັງບັນທຶກ...
              </>
            ) : isCompletedBooking ? (
              <>
                ຢືນຢັນກວດຊື່ຄູ
                <CheckIcon className="h-4 w-4" />
              </>
            ) : (
              <>
                ຢືນຢັນການເຂົ້າຮຽນ
                <CheckIcon className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      <AddSubstituteInstructorDialog
        sessionId={bookingId}
        sessionType="private_lesson"
        open={substituteDialogOpen}
        onOpenChange={setSubstituteDialogOpen}
        onSuccess={onSuccess}
        assignedInstructorIds={instructorIds}
        assignedInstructorNames={instructorNames}
      />
      <AddGuestInstructorDialog
        sessionId={bookingId}
        sessionType="private_lesson"
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
        onSuccess={onSuccess}
        assignedInstructorIds={instructorIds}
        assignedInstructorNames={instructorNames}
      />
    </div>
  );
}
